import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { PlusIcon } from '@/app/common/icons/arrows'
import { parseRequestError } from '@/app/common/utilities'
import { usePostProgramCatalogItem } from '@/queries/program_catalog/hooks/usePostProgramCatalogitem'
import { useToast } from '@/shad/ui/toast/use-toast'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { useSelector } from 'react-redux'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { SubmitButton } from '@/app/common/components/SubmitButton'

// Define the form schema with Zod
const formSchema = z.object({
  serviceFee: z
    .string()
    .min(1, { message: 'Service Fee is required' })
    .refine((val) => !isNaN(parseFloat(val)), {
      message: 'Service Fee must be a number',
    }),
})

// Type for form values
type FormValues = z.infer<typeof formSchema>

interface Props {
  itemName: string
  itemId: number
  itemCategoryId: number
  onAddItemSuccess: () => void
}

function AddItemPopup(props: Props) {
  const { itemName = 'Item', itemCategoryId, itemId, onAddItemSuccess } = props
  const programId = useSelector(selectSelectedProgram).id

  // Dialog state
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()

  const addItemMutation = usePostProgramCatalogItem({
    onSuccess: () => {
      toast({
        title: 'Item Added',
        description: `${itemName} has been added to your program catalog.`,
        variant: 'default',
      })
      setIsOpen(false)
      onAddItemSuccess()
    },
    onError: (error) => {
      toast({
        title: 'Error Adding Item',
        description: parseRequestError(error).message,
        variant: 'destructive',
      })
    },
  })

  // Initialize React Hook Form with Zod resolver
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serviceFee: '',
    },
  })

  // Handle dialog open state changes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      reset()
    }
  }

  // Form submission handler
  const onSubmit = (values: FormValues) => {
    const serviceFee = parseFloat(values.serviceFee)

    addItemMutation.mutate({
      service_fee: serviceFee,
      global_item_category_id: itemCategoryId, // Replace with actual category ID
      global_item_id: itemId, // Replace with actual item ID
      programId: programId,
    })

    reset()
  }

  // Custom handler for service fee input to only allow numeric values
  const handleServiceFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '' || /^(\d+\.?\d*|\.\d+)$/.test(value)) {
      e.target.value = value
    } else {
      e.preventDefault()
    }
  }

  // Otherwise, show the add button and dialog
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="flex items-center"
        >
          <PlusIcon className="mr-2 text-xs" /> Add
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Item</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
          <div className="space-y-1">
            <Label htmlFor="serviceFee">
              Service Fee (%)<span className="text-red-500">*</span>
            </Label>
            <Input
              id="serviceFee"
              {...register('serviceFee')}
              placeholder="Enter service fee percentage"
              className={errors.serviceFee ? 'border-red-500' : ''}
              onChange={handleServiceFeeChange}
            />
            {errors.serviceFee && (
              <p className="text-red-500 text-sm">
                {errors.serviceFee.message}
              </p>
            )}
          </div>

          <div className="text-sm text-gray-500">
            <p>
              Adding {itemName} to your program catalog will make it available
              for customers.
            </p>
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton status={addItemMutation.status} buttonTxt="Add" />
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddItemPopup
